import React from "react";
import { Link } from "react-router-dom";
import { ScrollText } from "lucide-react";
import { Logo } from "@/components/Logo";

const EFFECTIVE_DATE = "30 September 2026";
const SUPPORT_EMAIL = "admin@debiiapp.com";

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-20" data-testid={`terms-section-${id}`}>
      <h2 className="font-display text-lg md:text-xl font-bold text-foreground mt-8 mb-3">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

export default function TermsOfUse() {
  return (
    <div className="min-h-screen bg-background" data-testid="terms-of-use-page">
      <header className="sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur">
        <div className="max-w-2xl mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/" data-testid="terms-home-link" aria-label="Debii home">
            <Logo size={30} />
          </Link>
          <span className="text-xs font-semibold text-muted-foreground">Terms of Use</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 pb-20">
        <div className="mt-8 rounded-2xl bg-primary text-primary-foreground p-6">
          <div className="flex items-center gap-2">
            <ScrollText className="h-5 w-5 text-emerald-300" />
            <p className="font-display font-bold text-lg">Terms of Use</p>
          </div>
          <p className="mt-2 text-sm text-white/85 leading-relaxed">
            These terms govern your use of Debii, a personal tool for recording money you lend or borrow.
            Please read them carefully.
          </p>
          <p className="mt-3 text-xs text-white/70">Effective date: {EFFECTIVE_DATE}</p>
        </div>

        <div className="mt-6 text-sm leading-relaxed text-muted-foreground">
          <p>
            These Terms of Use (&ldquo;Terms&rdquo;) are a legal agreement between you and{" "}
            <strong className="text-foreground">Debii Technologies Limited</strong> (&ldquo;Debii&rdquo;,
            &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) governing your access to and use of the Debii
            application and website at <span className="text-foreground">debiiapp.com</span> (the
            &ldquo;Service&rdquo;). By creating an account or using the Service, you agree to these Terms. If you do
            not agree, please do not use the Service.
          </p>
        </div>

        <Section id="what-debii-is" title="1. What Debii is (and is not)">
          <p>
            Debii is a personal debt-tracking platform. It helps you privately record money you lend to others or
            borrow from them, send your own reminders, and optionally ask the other person to acknowledge a record.
          </p>
          <p className="text-foreground font-medium">Debii is not a financial institution. Debii does not:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>lend money or provide credit;</li>
            <li>act as a bank, wallet or payment intermediary;</li>
            <li>receive, hold, transfer or custody any funds on your behalf;</li>
            <li>collect debts for you or act as a debt-collection agency;</li>
            <li>guarantee that any person will acknowledge, dispute or repay a record.</li>
          </ul>
          <p>
            Any repayment is made directly between you and the other person. Debii only helps you record and track it.
          </p>
        </Section>

        <Section id="eligibility" title="2. Eligibility and your account">
          <p>
            You must be at least 18 years old to use Debii. You agree to provide accurate information when you
            register and to keep it up to date. You are responsible for all activity that happens under your account.
          </p>
        </Section>

        <Section id="responsibilities" title="3. Your responsibilities and accurate records">
          <ul className="list-disc pl-5 space-y-1">
            <li>You are solely responsible for the accuracy of the records you enter, including names, amounts, dates and repayment notes.</li>
            <li>You must have a genuine, lawful basis for recording and contacting any person you add, and for sending them reminders or links.</li>
            <li>You are responsible for how you use reminders, acknowledgement links and repayment links, and for complying with all laws that apply to you.</li>
            <li>Debii is a record-keeping tool; it does not verify the truth of the records you create or decide who owes what.</li>
          </ul>
        </Section>

        <Section id="acknowledgement" title="4. Acknowledgement and dispute features">
          <p>
            Debii lets you generate secure links so the other person can acknowledge or dispute a specific record, or
            view and report a repayment, without needing a Debii account. An acknowledgement or dispute is a response
            recorded through Debii between you and that person; it is not a legal determination, judgment or
            certification by Debii. Debii does not mediate, arbitrate or resolve disputes between you and any other
            person.
          </p>
        </Section>

        <Section id="prohibited" title="5. Prohibited conduct">
          <p>You agree not to use Debii to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>harass, threaten, defame or intimidate any person;</li>
            <li>record false, misleading or fraudulent information;</li>
            <li>add or contact people without a lawful basis, or send unsolicited or abusive messages;</li>
            <li>impersonate another person or misrepresent your identity;</li>
            <li>attempt to gain unauthorised access to the Service, other accounts or our systems;</li>
            <li>interfere with, disrupt or reverse-engineer the Service;</li>
            <li>use the Service for any unlawful purpose or in breach of these Terms.</li>
          </ul>
          <p>We may suspend or terminate accounts that breach these rules.</p>
        </Section>

        <Section id="security" title="6. Account security">
          <p>
            Keep your password confidential and use a strong, unique password. You are responsible for maintaining the
            security of your login credentials and for any activity under your account. Notify us promptly at{" "}
            <a className="text-primary font-medium" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> if you suspect
            unauthorised access.
          </p>
        </Section>

        <Section id="plus" title="7. Debii Plus subscription">
          <p>
            Debii offers an optional paid plan, <strong className="text-foreground">Debii Plus</strong>, which unlocks
            additional features. The core ability to receive and respond to acknowledgement requests is always free.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Debii Plus is an annual subscription billed once per year.</li>
            <li>Payments are processed by our payments partner, <strong className="text-foreground">Paystack</strong>. We do not store your full card details.</li>
            <li>Your Plus access runs until the expiry date shown in the app. It does <strong className="text-foreground">not</strong> renew automatically — you choose whether to renew by making a new payment before or after it expires.</li>
            <li>Pricing is shown in the app at the point of purchase and may change for future terms; any change will not affect a subscription period you have already paid for.</li>
          </ul>
        </Section>

        <Section id="cancellation" title="8. Cancellation and refunds">
          <p>
            Because Debii Plus does not auto-renew, you can simply choose not to renew — no cancellation step is
            required, and you will not be charged again automatically. If you stop renewing, your Plus features end
            when your current paid period expires, and your account continues on the free plan.
          </p>
          <p>
            Payments for a subscription period are generally non-refundable except where required by applicable law.
            If you believe you were charged in error, contact us at{" "}
            <a className="text-primary font-medium" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we will
            review your request.
          </p>
        </Section>

        <Section id="termination" title="9. Account termination">
          <p>
            You may delete your account at any time from within the app (Profile → Privacy → Delete Account). We may
            suspend or terminate your access if you breach these Terms, misuse the Service, or where required by law.
            On termination, your right to use the Service ends; our handling of your data is described in our{" "}
            <Link to="/privacy-policy" className="text-primary font-medium" data-testid="terms-privacy-link">Privacy Policy</Link>.
          </p>
        </Section>

        <Section id="disclaimer" title="10. Disclaimers">
          <p>
            The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo;, without warranties of any kind
            to the fullest extent permitted by law. Debii does not warrant that the Service will be uninterrupted,
            error-free or completely secure. Debii is not responsible for the conduct of any person you record,
            contact or transact with, or for whether any debt is acknowledged, disputed or repaid.
          </p>
        </Section>

        <Section id="liability" title="11. Limitation of liability">
          <p>
            To the maximum extent permitted by law, Debii and its directors, employees and partners will not be liable
            for any indirect, incidental, special, consequential or punitive damages, or for any loss of money, data,
            profits or goodwill, arising from your use of (or inability to use) the Service, or from any dealings
            between you and any other person. Nothing in these Terms excludes liability that cannot be excluded under
            applicable law.
          </p>
        </Section>

        <Section id="changes" title="12. Changes to these Terms">
          <p>
            We may update these Terms from time to time. When we make material changes, we will update the effective
            date above and, where appropriate, notify you within the app. Your continued use of the Service after
            changes take effect means you accept the updated Terms.
          </p>
        </Section>

        <Section id="law" title="13. Governing law">
          <p>
            These Terms are governed by the laws of the Federal Republic of Nigeria, and you agree to the exclusive
            jurisdiction of the courts of Nigeria for any dispute arising from these Terms or the Service, subject to
            any rights you have under applicable law.
          </p>
        </Section>

        <Section id="contact" title="14. Contact us">
          <p>
            Debii is operated by <strong className="text-foreground">Debii Technologies Limited</strong>. If you have
            any questions about these Terms, contact us at:
          </p>
          <p>
            <a className="text-primary font-medium" href={`mailto:${SUPPORT_EMAIL}`} data-testid="terms-contact-email">
              {SUPPORT_EMAIL}
            </a>
          </p>
          <p>
            See also our{" "}
            <Link to="/privacy-policy" className="text-primary font-medium" data-testid="terms-privacy-link-footer">Privacy Policy</Link>.
          </p>
        </Section>

        <p className="mt-10 text-xs text-muted-foreground">Last updated: {EFFECTIVE_DATE}</p>
      </main>
    </div>
  );
}
